// src/components/exam-player/renderers/EssayRenderer.jsx
import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

function countWords(str = "") {
  // شمارش کلمات مبتنی بر جداسازی با فاصله/لاین‌بریک (سازگار با زبان‌ها)
  const txt = String(str).replace(/\s+/g, " ").trim();
  if (!txt) return 0;
  // هر چیزی که با فاصله جدا شده را یک کلمه حساب می‌کنیم
  return txt.split(" ").length;
}

export default function EssayRenderer({
  item,
  onAnswerChange,
  initialAnswers,
  questionNumber,
}) {
  const { t } = useTranslation();

  const minW = Number.isFinite(Number(item?.body?.minWords))
    ? Number(item.body.minWords)
    : undefined;
  const maxW = Number.isFinite(Number(item?.body?.maxWords))
    ? Number(item.body.maxWords)
    : undefined;

  // مقدار اولیه: می‌تونه string باشه یا شیء {text, wordCount}
  const initialText = useMemo(() => {
    if (typeof initialAnswers === "string") return initialAnswers;
    if (initialAnswers && typeof initialAnswers.text === "string")
      return initialAnswers.text;
    return "";
  }, [initialAnswers]);

  const [text, setText] = useState(initialText);
  const wc = countWords(text);

  useEffect(() => {
    setText(initialText);
  }, [initialText]);

  // هر تغییر را به والد پاس بده
  useEffect(() => {
    onAnswerChange?.({ text, wordCount: wc });
  }, [text, wc, onAnswerChange]);

  const underMin = typeof minW === "number" && wc < minW;
  const overMax = typeof maxW === "number" && wc > maxW;
  const outOfRange = underMin || overMax;

  return (
    <div className="space-y-3">
      {/* شماره سؤال در صورت وجود */}
      {typeof questionNumber === "number" && (
        <div
          aria-hidden
          className="inline-flex h-6 min-w-6 items-center justify-center rounded-full border text-xs font-medium px-2"
        >
          {questionNumber}
        </div>
      )}

      <textarea
        value={text}
        onChange={(e) => setText(e.target.value)}
        onBlur={(e) => {
          // trim نهایی هنگام خروج از فوکوس
          const trimmed = e.target.value.replace(/\s+/g, " ").trim();
          if (trimmed !== text) setText(trimmed);
        }}
        rows={8}
        className={`w-full rounded-lg border px-3 py-2 focus:outline-none focus:ring-1 ${
          outOfRange
            ? "border-orange-300 focus:ring-orange-400"
            : "border-gray-300 focus:ring-indigo-400"
        }`}
        placeholder={t("essay.type_here", "Type your answer here...")}
      />

      <div className="flex items-center justify-between text-xs">
        <div
          className={`font-medium ${
            outOfRange ? "text-orange-600" : "text-gray-600"
          }`}
        >
          {t("essay.words_count", "Words count")}: {wc}
        </div>

        <div className="text-[11px] text-gray-500">
          {/* نمایش قواعد حداقل/حداکثر در صورت وجود */}
          {typeof minW === "number" && typeof maxW === "number" ? (
            <span>
              {t("essay.range_hint", "Allowed")} {minW}–{maxW}{" "}
              {t("essay.words", "words")}
              {underMin && " — " + t("essay.too_few", "too few")}
              {overMax && " — " + t("essay.too_many", "too many")}
            </span>
          ) : typeof minW === "number" ? (
            <span>
              {t("essay.min_hint", "Minimum")} {minW}{" "}
              {t("essay.words", "words")}
              {underMin && " — " + t("essay.too_few", "too few")}
            </span>
          ) : typeof maxW === "number" ? (
            <span>
              {t("essay.max_hint", "Maximum")} {maxW}{" "}
              {t("essay.words", "words")}
              {overMax && " — " + t("essay.too_many", "too many")}
            </span>
          ) : null}
        </div>
      </div>
    </div>
  );
}
